import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Zap, TrendingUp, Flame, Timer, Route as RouteIcon, ChevronRight, Snowflake, Trophy } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDistance, fmtDuration, fmtPace, fmtDate, todayStr } from "../lib/format";

const StatCard = ({ label, value, unit, icon: Icon, accent, testid }) => (
  <div data-testid={testid} className="bg-[#121212] border border-white/10 rounded-2xl p-5 h-full">
    <div className="flex items-center justify-between mb-3">
      <span className="data-label">{label}</span>
      <Icon className={`w-4 h-4 ${accent ? "text-[#CCFF00]" : "text-neutral-500"}`} />
    </div>
    <div className="font-display font-black text-3xl tracking-tighter tabular-nums">
      {value}
      {unit && <span className="text-sm text-neutral-500 ml-1 font-bold">{unit}</span>}
    </div>
  </div>
);

function StreakCard({ streak }) {
  if (!streak) return null;
  const days = streak.current || 0;
  const freezes = streak.freezes || 0;
  const max = streak.max_freezes || 2;
  return (
    <div data-testid="streak-card" className="relative overflow-hidden bg-[#121212] border border-white/10 rounded-3xl p-6 mb-4">
      <div className="absolute -right-6 -top-6 opacity-10">
        <Flame className="w-40 h-40 text-[#CCFF00]" />
      </div>
      <div className="relative flex items-end justify-between">
        <div>
          <span className="data-label">Aktuelle Streak</span>
          <div className="flex items-baseline gap-2 mt-1">
            <span data-testid="streak-days" className="font-display font-black text-6xl tracking-tighter leading-none text-[#CCFF00]">
              {days}
            </span>
            <span className="font-display font-bold text-lg text-neutral-400">Tage</span>
          </div>
          <div className="text-xs text-neutral-500 mt-2">Rekord: {streak.best || 0} Tage</div>
        </div>
        <div className="text-right">
          <span className="data-label">Eis</span>
          <div data-testid="streak-freezes" className="flex items-center gap-1 mt-2 justify-end">
            {Array.from({ length: max }).map((_, i) => (
              <Snowflake
                key={i}
                className={`w-6 h-6 ${i < freezes ? "text-[#66D9FF]" : "text-neutral-700"}`}
                fill={i < freezes ? "#66D9FF" : "none"}
              />
            ))}
          </div>
          <div className="text-[10px] text-neutral-500 mt-1 max-w-[120px]">
            {streak.runs_per_freeze || 3} Läufe = 1 Eis
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [runs, setRuns] = useState([]);
  const [streak, setStreak] = useState(null);
  const [board, setBoard] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const t = todayStr();
    api.get("/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/runs").then(({ data }) => setRuns(data)).catch(() => {});
    api.get(`/streak?today=${t}`).then(({ data }) => setStreak(data)).catch(() => {});
    api.get(`/social/leaderboard?today=${t}`).then(({ data }) => setBoard(data)).catch(() => {});
  }, []);

  const chartData = [...runs]
    .slice(0, 10)
    .reverse()
    .map((r, i) => ({ name: `#${i + 1}`, km: +(r.distance_m / 1000).toFixed(2) }));

  return (
    <div className="max-w-lg mx-auto px-5 pt-safe">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <Zap className="w-4 h-4 text-[#CCFF00]" fill="#CCFF00" />
            <span className="data-label">Paceline</span>
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter leading-none">
            Hallo, {user?.name?.split(" ")[0]}.
          </h1>
        </div>
        <button
          data-testid="dashboard-start-btn"
          onClick={() => navigate("/record")}
          className="bg-[#CCFF00] text-black font-display font-black rounded-full px-5 py-3 text-sm active:scale-95 transition-transform"
        >
          Lauf starten
        </button>
      </div>

      <StreakCard streak={streak} />

      <div className="grid grid-cols-2 gap-4 mb-4">
        <StatCard testid="stat-distance" label="Gesamt Distanz" value={fmtDistance(stats?.total_distance_m)} unit="km" icon={RouteIcon} accent />
        <StatCard testid="stat-runs" label="Läufe" value={stats?.total_runs ?? 0} icon={TrendingUp} />
        <StatCard testid="stat-time" label="Gesamt Zeit" value={fmtDuration(stats?.total_duration_s)} icon={Timer} />
        <StatCard testid="stat-calories" label="Kalorien" value={Math.round(stats?.total_calories || 0)} unit="kcal" icon={Flame} />
      </div>

      {chartData.length > 1 && (
        <div className="bg-[#121212] border border-white/10 rounded-2xl p-5 mb-4" data-testid="progress-chart">
          <div className="flex items-center justify-between mb-4">
            <span className="data-label">Distanz-Verlauf</span>
            <span className="text-xs text-neutral-500">letzte {chartData.length} Läufe</span>
          </div>
          <ResponsiveContainer width="100%" height={140}>
            <LineChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
              <XAxis dataKey="name" stroke="#3f3f46" tick={{ fill: "#71717a", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: "#1A1A1A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }}
                labelStyle={{ color: "#71717a" }}
                formatter={(v) => [`${v} km`, "Distanz"]}
              />
              <Line type="monotone" dataKey="km" stroke="#CCFF00" strokeWidth={3} dot={{ r: 3, fill: "#CCFF00" }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {board.length > 1 && (
        <div className="bg-[#121212] border border-white/10 rounded-2xl p-5 mb-4" data-testid="weekly-leaderboard">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-4 h-4 text-[#CCFF00]" />
            <span className="data-label">Wochen-Ranking</span>
          </div>
          <div className="space-y-3">
            {board.map((row, i) => (
              <div key={row.id} className="flex items-center gap-3">
                <span className={`w-6 font-display font-black ${i === 0 ? "text-[#CCFF00]" : "text-neutral-500"}`}>{i + 1}</span>
                <span className={`flex-1 font-semibold truncate ${row.is_me ? "text-white" : "text-neutral-300"}`}>
                  {row.name} {row.is_me && <span className="text-xs text-[#CCFF00]">(Du)</span>}
                </span>
                <span className="font-display font-black tabular-nums">{fmtDistance(row.weekly_distance_m)} <span className="text-xs text-neutral-500">km</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <span className="data-label">Letzte Läufe</span>
        <button onClick={() => navigate("/history")} data-testid="see-all-runs" className="text-[#CCFF00] text-xs font-bold flex items-center">
          Alle <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      {runs.length === 0 ? (
        <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 text-center" data-testid="dashboard-empty">
          <p className="text-neutral-400 text-sm mb-4">Noch keine Läufe. Zeit loszulegen!</p>
          <button onClick={() => navigate("/record")} className="text-[#CCFF00] font-bold text-sm">
            Ersten Lauf starten →
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {runs.slice(0, 3).map((r) => (
            <div key={r.id} className="bg-[#121212] border border-white/10 rounded-2xl p-4 flex items-center justify-between">
              <div>
                <div className="font-display font-black text-xl tracking-tighter">
                  {fmtDistance(r.distance_m)} <span className="text-sm text-neutral-500">km</span>
                  {r.type === "walk" && <span className="text-xs text-neutral-500 ml-2">Spaziergang</span>}
                </div>
                <div className="text-xs text-neutral-500 mt-0.5">{fmtDate(r.started_at)}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold">{fmtDuration(r.duration_s)}</div>
                <div className="text-xs text-neutral-500">{fmtPace(r.pace_min_km)} /km</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
